// Cleanlist-level dedup exclusion helpers (CP-8 — People-search "exclude
// people already in my Cleanlist").
//
// Bridges the SearchFilters ``excludeInCleanlist`` control to the
// /lead-lists/exclusion-identifiers backend contract (BE CP-4c) and back into
// the Crustdata exclude inputs. Kept out of ``people-search-hook`` so the pure
// mapping (scope + list_ids → request body) and the LinkedIn-URL merge/dedup
// are small, self-contained, testable units that don't drag the hook's
// RTK/mutation graph along — mirroring ``crm-exclusions.util``.

import type { SearchFilters } from '@/types/search-filters';
import type { LeadListExclusionIdentifiersRequest } from '@/store/features/lead-lists.feature';

// ─── Control → request-body mapping ─────────────────────────────────

/** Defensive read of the Cleanlist-dedup control. A saved/hydrated search may
 *  predate ``excludeInCleanlist`` — default to ``'off'`` (migration-safe). */
export function getCleanlistExclusionScope(
  filters: SearchFilters
): 'off' | 'any' | 'list' {
  return filters.excludeInCleanlist ?? 'off';
}

/** True iff the user has the Cleanlist dedup enabled (either scope). */
export function hasCleanlistExclusion(filters: SearchFilters): boolean {
  return getCleanlistExclusionScope(filters) !== 'off';
}

/**
 * Map the Cleanlist-dedup control to the exact
 * /lead-lists/exclusion-identifiers request body, or ``null`` when the
 * control is off / unresolvable:
 *
 *   excludeInCleanlist === 'off'   → null (no resolve).
 *   excludeInCleanlist === 'any'   → { scope: 'any' } (every org lead).
 *   excludeInCleanlist === 'list'  → { scope: 'list', list_ids: [...] }
 *                                    using the trimmed, de-duplicated
 *                                    ``excludeInCleanlistListIds``. Returns
 *                                    null when that resolves to zero ids —
 *                                    a list scope with no lists selected has
 *                                    nothing to resolve (and the BE would
 *                                    reject an empty list_ids).
 */
export function buildCleanlistExclusionRequest(
  filters: SearchFilters
): LeadListExclusionIdentifiersRequest | null {
  const scope = getCleanlistExclusionScope(filters);
  if (scope === 'off') return null;
  if (scope === 'any') return { scope: 'any' };

  // scope === 'list'
  const ids = dedupeListIds(filters.excludeInCleanlistListIds ?? []);
  if (ids.length === 0) return null;
  return { scope: 'list', list_ids: ids };
}

/** Trim, drop blanks, and de-duplicate a list-id array (stable order). */
export function dedupeListIds(ids: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    if (typeof raw !== 'string') continue;
    const id = raw.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

// ─── Identifier merge ───────────────────────────────────────────────

/**
 * Merge a Cleanlist ``linkedin_urls`` set into an existing exclude-URL set
 * (the CRM linkedin_urls + the manual ``excludeProfileUrls``), de-duplicated
 * case-insensitively but preserving the first occurrence's original casing.
 *
 * Returns the union as a fresh array — never mutates the inputs. Used to feed
 * the SAME Crustdata ``not_in`` exclude that the CRM excludes use, so a person
 * is suppressed if they match ANY exclude source.
 *
 * NOTE: only LinkedIn URLs are merged here. Crustdata person-search has no
 * email exclude column, so the response's ``emails`` are intentionally NOT
 * merged into the wire exclude — LinkedIn URL is the reliable join key.
 */
export function mergeCleanlistLinkedInUrls(
  existing: ReadonlyArray<string>,
  cleanlistUrls: ReadonlyArray<string>
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of [existing, cleanlistUrls]) {
    for (const raw of list) {
      if (typeof raw !== 'string') continue;
      const trimmed = raw.trim();
      if (!trimmed) continue;
      const norm = trimmed.toLowerCase();
      if (seen.has(norm)) continue;
      seen.add(norm);
      out.push(trimmed);
    }
  }
  return out;
}
